import React, { useEffect, useState } from "react";
import {
  getAdditionDiductionType,
  getDeductionWiseReport,
} from "../../services/dataService";
import Loading from "../../components/common/Loading";
import ReactDataTable from "react-data-table-component";
import jsPDF from "jspdf";
import "jspdf-autotable";
import Breadcrumb from "../../components/common/Breadcrumb";
import { getSecureItem } from "../../services/SecureStorage";

function DeductionWiseReport() {
  const isAdmin = getSecureItem("IsAdmin");
  const UID = getSecureItem("userId");
  const departmentIds = getSecureItem("DepartmentId") || "";
  const dep = departmentIds.split(",")[0] || null;

  const [additionDeductionType, setAdditionDeductionType] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [allData, setAllData] = useState([]);
  const [SelectedId, setSelectedId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterOptions, setFilterOptions] = useState([]);
  const [selectedFilterValue, setSelectedFilterValue] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchAdditionDeductionType = async () => {
      try {
        setIsLoading(true);
        const response = await getAdditionDiductionType(UID);
        setAdditionDeductionType(response.data.data || []);
      } catch (error) {
        console.error("Error fetching addition/deduction types:", error);
        alert("Failed to fetch deduction types. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchAdditionDeductionType();
  }, [UID]);

  const FetchDeductionWiseReport = async () => {
    if (!SelectedId || !fromDate || !toDate) {
      alert("Please select month and deduction type.");
      return;
    }

    try {
      setIsLoading(true);
      const data = {
        UserId: UID,
        FromDate: fromDate,
        ToDate: toDate,
        SelectedId: SelectedId,
      };

      const response = await getDeductionWiseReport(data);
      const details =
        isAdmin == 1
          ? response.data.data
          : response.data.data.filter((el) => el.DepartmentId == dep);
      setAllData(details || []);
    } catch (error) {
      console.error("Error fetching deduction-wise report:", error);
      alert("Failed to fetch report. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewClick = () => {
    FetchDeductionWiseReport();
  };

  const handleChangeMonth = (e) => {
    const value = e.target.value;
    setSelectedMonth(value);
    const firstDay = `${value}-01`;
    const [year, month] = value.split("-").map(Number);
    const lastDayOfMonth = new Date(year, month, 0).getDate();
    const lastDay = `${value}-${String(lastDayOfMonth).padStart(2, "0")}`;
    setFromDate(firstDay);
    setToDate(lastDay);
  };

  const handleFilterTypeChange = (e) => {
    const value = e.target.value;
    setFilterType(value);
    const options = [
      ...new Set(allData.map((el) => el[`${value}Name`])),
    ].filter((opt) => opt);
    setFilterOptions(options);
    setSelectedFilterValue("");
  };

  const handleClearFilter = () => {
    setFilterType("");
    setFilterOptions([]);
    setSelectedFilterValue("");
    setSearchValue("");
  };

  const handleDownloadPDF = () => {
    if (!allData.length) {
      alert("No data available to download.");
      return;
    }
    const groupedByDept = filteredItems.reduce((acc, item) => {
      if (!acc[item.DepartmentName]) acc[item.DepartmentName] = [];
      acc[item.DepartmentName].push(item);
      return acc;
    }, {});

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });
    doc.setFontSize(18);
    doc.setTextColor(0, 0, 0);
    doc.text(
      `${(allData[0]?.type || "Deduction").toUpperCase()} REPORT`,
      148.5,
      15,
      {
        align: "center",
      }
    );

    // Add filter information only if filterType exists and is not department
    let currentY = 25;
    if (
      filterType &&
      selectedFilterValue &&
      filterType.toLowerCase() !== "department"
    ) {
      doc.setFontSize(12);
      doc.text(`Filtered by ${filterType}: ${selectedFilterValue}`, 20, 25);
      currentY = 35;
    }

    // Calculate grand total
    const grandTotal = filteredItems.reduce(
      (sum, item) => sum + (parseFloat(item.CalculatedAmount) || 0),
      0
    );

    for (const [dept, items] of Object.entries(groupedByDept)) {
      // Add department header
      doc.setFontSize(12);
      doc.text(`Department: ${dept}`, 20, currentY);
      currentY += 5;

      // Calculate department total
      const deptTotal = items.reduce(
        (sum, item) => sum + (parseFloat(item.CalculatedAmount) || 0),
        0
      );

      // Store table data
      const tableData = items.map((item, i) => [
        i + 1,
        item.Name,
        item.Code,
        item.CategoryName,
        item.DesignationName,
        parseFloat(item.CalculatedAmount).toFixed(2),
      ]);

      // Calculate equal margins (A4 landscape width = 297mm)
      const pageWidth = 297;
      const totalTableWidth = 255; // Sum of column widths (20 + 60 + 35 + 50 + 60 + 30)
      const margin = (pageWidth - totalTableWidth) / 2; // Equal margins on both sides

      // Render main table
      doc.autoTable({
        head: [["Sl No", "Name", "Code", "Category", "Designation", "Amount"]],
        body: tableData,
        startY: currentY,
        margin: { left: margin, right: margin },
        theme: "plain",
        styles: {
          fontSize: 9,
          cellPadding: 2,
          textColor: [0, 0, 0],
          fillColor: [255, 255, 255],
          overflow: "linebreak",
          lineWidth: 0.2,
          lineColor: [0, 0, 0],
        },
        headStyles: {
          fillColor: [200, 200, 200],
          textColor: [0, 0, 0],
          fontSize: 9,
          cellPadding: 2,
          lineWidth: 0.2,
          lineColor: [0, 0, 0],
          fontStyle: "bold",
        },
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 60 },
          2: { cellWidth: 35 },
          3: { cellWidth: 50 },
          4: { cellWidth: 60 },
          5: { cellWidth: 30, halign: "right" },
        },
        horizontalPageBreak: true,
      });

      // Add department total row immediately after the table
      const tableEndY = doc.lastAutoTable.finalY;
      doc.autoTable({
        startY: tableEndY,
        body: [["", "", "", "", "Total", deptTotal.toFixed(2)]],
        margin: { left: margin, right: margin },
        theme: "plain",
        styles: {
          fontSize: 9,
          cellPadding: 2,
          textColor: [0, 0, 0],
          fillColor: [255, 255, 255],
          fontStyle: "bold",
          lineWidth: 0.2,
          lineColor: [0, 0, 0],
        },
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 60 },
          2: { cellWidth: 35 },
          3: { cellWidth: 50 },
          4: { cellWidth: 60 },
          5: { cellWidth: 30, halign: "right" },
        },
      });

      // Update currentY for next department
      currentY = doc.lastAutoTable.finalY + 10;
    }

    // Add grand total row at the end
    const pageWidth = 297;
    const totalTableWidth = 255;
    const margin = (pageWidth - totalTableWidth) / 2;

    doc.autoTable({
      startY: currentY,
      body: [["", "", "", "", "Grand Total", grandTotal.toFixed(2)]],
      margin: { left: margin, right: margin },
      theme: "plain",
      styles: {
        fontSize: 10,
        cellPadding: 3,
        textColor: [0, 0, 0],
        fillColor: [220, 220, 220],
        fontStyle: "bold",
        lineWidth: 0.3,
        lineColor: [0, 0, 0],
      },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 60 },
        2: { cellWidth: 35 },
        3: { cellWidth: 50 },
        4: { cellWidth: 60 },
        5: { cellWidth: 30, halign: "right" },
      },
    });

    doc.save("Deduction_Report.pdf");
  };
  const filteredItems = allData.filter((item) => {
    const search = searchValue.toLowerCase();
    return (
      (!filterType || item[`${filterType}Name`] === selectedFilterValue) &&
      (item.Name?.toLowerCase().includes(search) ||
        item.Code?.toString().toLowerCase().includes(search) ||
        item.CalculatedAmount?.toString().toLowerCase().includes(search))
    );
  });

  const columnsFilter = [
    { name: "Sl No", cell: (row, index) => index + 1, width: "70px" },
    { name: "Employee Name", selector: (row) => row.Name, sortable: true },
    { name: "Emp Code", selector: (row) => row.Code },
    { name: "Department", selector: (row) => row.DepartmentName },
    { name: "Profit Center", selector: (row) => row.ProfitcenterName },
    { name: "Category", selector: (row) => row.CategoryName },
    { name: "Designation", selector: (row) => row.DesignationName },
    { name: "Amount", selector: (row) => row.CalculatedAmount, right: true },
  ];

  return (
    <div className="p-6 space-y-4">
      {isLoading && <Loading />}
      <Breadcrumb title="Addition Deduction Report" />

      <div className="flex flex-wrap gap-4 items-center">
        <label className="flex flex-col">
          Choose Month:
          <input
            type="month"
            value={selectedMonth}
            onChange={handleChangeMonth}
            className="border rounded px-2 py-1"
          />
        </label>

        <label className="flex flex-col">
          Choose Addition/Deduction:
          <select
            value={SelectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="border rounded px-2 py-1"
          >
            <option value="">-- Select --</option>
            {additionDeductionType.map((el) => (
              <option key={el.Id} value={el.Id}>
                {el.Name}
              </option>
            ))}
          </select>
        </label>

        <button
          onClick={handleViewClick}
          className="bg-blue text-white mt-3 px-4 py-2 rounded"
        >
          Show
        </button>

        <input
          type="search"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder="Search..."
          className="border rounded px-2 py-1"
        />

        {isAdmin == 1 && (
          <>
            <select
              onChange={handleFilterTypeChange}
              value={filterType}
              className="border rounded px-2 py-1"
            >
              <option value="">-- Filter By --</option>
              <option value="Department">Department</option>
              <option value="Profitcenter">Profit Center</option>
              <option value="Category">Category</option>
              <option value="Designation">Designation</option>
            </select>

            <select
              value={selectedFilterValue}
              onChange={(e) => setSelectedFilterValue(e.target.value)}
              className="border rounded px-2 py-1"
            >
              <option value="">-- Select Option --</option>
              {filterOptions.map((opt, idx) => (
                <option key={idx} value={opt}>
                  {opt}
                </option>
              ))}
            </select>

            <button
              onClick={handleClearFilter}
              className="bg-gray-500 text-white px-4 py-2 rounded"
            >
              Clear Filter
            </button>
          </>
        )}

        <button
          onClick={handleDownloadPDF}
          className="bg-green-600 text-white px-4 py-2 rounded"
        >
          Download PDF
        </button>
      </div>

      <div className="react-data-table">
        <ReactDataTable
          columns={columnsFilter}
          data={filteredItems}
          pagination
          highlightOnHover
          striped
          noDataComponent="No data available. Please select a month and deduction type."
        />
      </div>
    </div>
  );
}

export default DeductionWiseReport;
